/**
 * Accidentes.js — Thin orchestrator.
 * All section logic lives in ./accidentes/*.jsx
 */
import React, { useState } from 'react';
import { AlertTriangle, Upload, FileText, BarChart3, Sparkles, Clock } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useUserStore } from '@/store/userStore';
import { ListadoSection } from './accidentes/Listado';
import { KpisSection } from './accidentes/Kpis';
import { CorrelacionSection } from './accidentes/Correlacion';
import { BajasPendientesSection } from './accidentes/Bajas';
import { InformesSection } from './accidentes/Informes';
import { UploadSection } from './accidentes/Upload';
import { HuerfanosSection } from './accidentes/Huerfanos';

const ALLOWED = ['dalonso@valorizasm.com', 'lolivares@valorizasm.com', 'abgarzon@valorizasm.com', 'seguridadysalud@valorizasm.com'];

export default function Accidentes() {
  const { token, user } = useUserStore();
  const isAllowed = user?.role === 'superadmin' || user?.role === 'admin' || ALLOWED.includes((user?.email || '').toLowerCase());
  const [tab, setTab] = useState('listado');

  if (!isAllowed) {
    return (
      <div className="p-8 text-center" data-testid="accidentes-no-access">
        <AlertTriangle size={48} style={{ color: '#F44336' }} className="mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Acceso restringido</h2>
        <p style={{ color: 'var(--vz-text-muted)' }}>
          El módulo de Accidentes está reservado para usuarios autorizados (Dirección, Prevención).
        </p>
      </div>
    );
  }

  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="font-montserrat text-2xl md:text-3xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>
          Accidentes laborales
        </h1>
        <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>
          Partes Delt@ + correlación con OPS, Liderazgo y Near Miss + informes con IA (Claude Sonnet 4.6)
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-7 max-w-4xl">
          <TabsTrigger value="listado" data-testid="acc-tab-listado"><FileText size={12} className="mr-1.5" /> Listado</TabsTrigger>
          <TabsTrigger value="kpis" data-testid="acc-tab-kpis"><BarChart3 size={12} className="mr-1.5" /> KPIs</TabsTrigger>
          <TabsTrigger value="correlacion" data-testid="acc-tab-correlacion"><Sparkles size={12} className="mr-1.5" /> Correlación</TabsTrigger>
          <TabsTrigger value="bajas" data-testid="acc-tab-bajas"><Clock size={12} className="mr-1.5" /> Bajas pendientes</TabsTrigger>
          <TabsTrigger value="informes" data-testid="acc-tab-informes"><Sparkles size={12} className="mr-1.5" /> Informes IA</TabsTrigger>
          <TabsTrigger value="upload" data-testid="acc-tab-upload"><Upload size={12} className="mr-1.5" /> Subir CSV</TabsTrigger>
          <TabsTrigger value="huerfanos" data-testid="acc-tab-huerfanos"><AlertTriangle size={12} className="mr-1.5" /> Sin centro</TabsTrigger>
        </TabsList>
        <TabsContent value="listado" className="mt-6"><ListadoSection authHeader={authHeader} /></TabsContent>
        <TabsContent value="kpis" className="mt-6"><KpisSection authHeader={authHeader} /></TabsContent>
        <TabsContent value="correlacion" className="mt-6"><CorrelacionSection authHeader={authHeader} /></TabsContent>
        <TabsContent value="bajas" className="mt-6"><BajasPendientesSection authHeader={authHeader} /></TabsContent>
        <TabsContent value="informes" className="mt-6"><InformesSection authHeader={authHeader} /></TabsContent>
        <TabsContent value="upload" className="mt-6"><UploadSection authHeader={authHeader} /></TabsContent>
        <TabsContent value="huerfanos" className="mt-6"><HuerfanosSection authHeader={authHeader} /></TabsContent>
      </Tabs>
    </div>
  );
}
